/**
 * Tolerant JSON helpers shared by the protocol parsers - the TS counterpart of
 * `AssistProtocol.cs` / `HostLinkStatus.cs`'s hand-rolled tolerant reads.
 *
 * Unlike Newtonsoft, `JSON.parse` never coerces ISO-8601 strings into Date
 * values mid-parse, so the C# `DateParseHandling.None` workaround has no TS
 * equivalent and must not be "ported". Scalars stay text; instants are parsed
 * on demand by {@link readInstant}.
 */

/** Parse a JSON object body. Null for non-object or malformed JSON. */
export function parseJsonObject(json: string): Record<string, unknown> | null {
  if (!json) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
  return parsed as Record<string, unknown>;
}

/** Read a string field; empty string when absent or not a string. */
export function readString(root: Record<string, unknown>, key: string): string {
  const value = root[key];
  return typeof value === 'string' ? value : '';
}

/** Read a strict-boolean field; false when absent or not a boolean. */
export function readBoolean(root: Record<string, unknown>, key: string): boolean {
  return root[key] === true;
}

/** Read a nested object field; null when absent or not an object. */
export function readObject(root: Record<string, unknown>, key: string): Record<string, unknown> | null {
  const value = root[key];
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function nullIfEmpty(value: string): string | null {
  return value === '' ? null : value;
}

/**
 * Parse a contract instant (ISO-8601 string) into epoch milliseconds. The
 * contract pattern-checks these rather than parsing them; here the value only
 * has to become a point in time, and a value that does not is simply absent -
 * never an exception in front of a trainee.
 */
export function readInstant(root: Record<string, unknown>, key: string): number | null {
  const raw = readString(root, key);
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
}
